import { useEffect, type ReactNode } from 'react';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { useAuth } from '../context/AuthContext';

type RequireAuthProps = {
  children: ReactNode;
};

/**
 * Renders children only when OAuth login has succeeded.
 * Uses SDK-driven redirect for unauthenticated users; shows MUI fallback states.
 */
const RequireAuth = ({ children }: RequireAuthProps): JSX.Element => {
  const { authenticated, loading, error, login, sdk } = useAuth();

  useEffect(() => {
    if (loading || authenticated || error) return;
    const isCallback =
      sdk && 'isLoginCallback' in sdk ? sdk.isLoginCallback() : false;
    if (!isCallback) {
      void login();
    }
  }, [loading, authenticated, error, login, sdk]);

  if (loading) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minHeight="100vh"
        gap={2}
      >
        <CircularProgress />
        <Typography color="text.secondary">Checking session…</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minHeight="100vh"
        gap={2}
        px={2}
      >
        <Typography variant="h6" align="center">
          Sign-in failed
        </Typography>
        <Typography color="text.secondary" align="center">
          {error}
        </Typography>
        <Button variant="contained" onClick={() => void login()}>
          Try again
        </Button>
      </Box>
    );
  }

  if (!authenticated) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minHeight="100vh"
        gap={2}
      >
        <CircularProgress />
        <Typography color="text.secondary">
          Нэвтрэх хуудас руу шилжиж байна…
        </Typography>
        <Button variant="outlined" onClick={() => void login()}>
          Одоо нэвтрэх
        </Button>
      </Box>
    );
  }

  return <>{children}</>;
};

export default RequireAuth;
